import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { ButtonComponent, DatepickerComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { AUTH_SERVICE_FREQUENCIES } from '../../../../shared/constants/static-data.constants';
import { normalizeServiceFrequency } from '../../../../shared/utils/auth-service-detail.util';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';

/** A positive number carrying at most three decimal places. */
const THREE_DECIMAL_PATTERN = /^\d+(\.\d{1,3})?$/;

export interface AuthServiceDetailPayload {
  authorizationProviderId: any;
  serviceDetail: string;
  frequency: string;
  units: number;
  price: number;
  startDate: string;
  endDate: string;
}

/**
 * Add/Edit form for a single service detail under a provider auth allocation.
 * Shared by the authorization detail page (expanded allocation row) and the
 * Service Details step of the add-authorization wizard.
 */
@Component({
  selector: 'ppl-auth-service-detail-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgZorroModule,
    NzFormDirective,
    ButtonComponent,
    DatepickerComponent
  ],
  templateUrl: './auth-service-detail-form.component.html',
})
export class AuthServiceDetailFormComponent implements OnInit {
  /** Existing record when editing; null when adding. */
  @Input() serviceDetail: any = null;
  /** Provider allocations to pick from. Leave empty to bind to a single allocation. */
  @Input() allocations: any[] = [];
  /** Allocation the service detail belongs to when no allocation dropdown is shown. */
  @Input() authorizationProviderId: any = null;
  @Output() save = new EventEmitter<AuthServiceDetailPayload>();
  @Output() deactivate = new EventEmitter<void>();

  readonly frequencies = AUTH_SERVICE_FREQUENCIES;
  serviceDetailForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dateFormatter: DateTimeFormatterService,
  ) { }

  get showAllocations() {
    return this.allocations.length > 0;
  }

  get isEdit() {
    return !!this.serviceDetail?.authProviderServiceId;
  }

  ngOnInit(): void {
    this.serviceDetailForm = this.fb.group({
      authorizationProviderId: [
        this.serviceDetail?.authorizationProviderId ?? this.authorizationProviderId,
        this.showAllocations ? Validators.required : [],
      ],
      serviceDetail: [this.serviceDetail?.serviceDetail ?? null, Validators.required],
      frequency: [normalizeServiceFrequency(this.serviceDetail?.frequency), Validators.required],
      // Units and price both allow up to three decimal places and must be above zero.
      units: [
        this.serviceDetail?.units ?? null,
        [Validators.required, Validators.min(0.001), Validators.max(9999), Validators.pattern(THREE_DECIMAL_PATTERN)],
      ],
      price: [
        this.serviceDetail?.price ?? null,
        [Validators.required, Validators.min(0.001), Validators.pattern(THREE_DECIMAL_PATTERN)],
      ],
      startDate: [this.serviceDetail?.startDate ? this.dateFormatter.formatStringToDate(this.serviceDetail?.startDate) : null, Validators.required],
      endDate: [this.serviceDetail?.endDate ? this.dateFormatter.formatStringToDate(this.serviceDetail?.endDate) : null, Validators.required],
    });
  }

  onSubmit() {
    if (this.serviceDetailForm.invalid) {
      this.serviceDetailForm.markAllAsTouched();
      return;
    }

    this.save.emit({
      authorizationProviderId: this.serviceDetailForm.value.authorizationProviderId,
      serviceDetail: (this.serviceDetailForm.value.serviceDetail ?? '').trim(),
      frequency: this.serviceDetailForm.value.frequency,
      units: Number(this.serviceDetailForm.value.units),
      price: Number(this.serviceDetailForm.value.price),
      startDate: this.dateFormatter.formatToString(this.serviceDetailForm.value.startDate),
      endDate: this.dateFormatter.formatToString(this.serviceDetailForm.value.endDate),
    });
  }
}
